import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import styled from 'styled-components';

import {Alert, FlexColumn, FlexRow, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {AlertType} from '@forecast-it/design-system/dist/forecast-v2/components/data-display/alert/types';
import {tokens as inputTokens} from '@forecast-it/design-system/dist/forecast-v2/components/inputs/input/input.tokens';

import {useForecastFetchQuery} from '../../../../../hooks/useForecastFetchQuery';
import {convertMinutesToFullHour} from '../../../../../util/convertMinutesToFullHour';
import {RetainerPeriodStatus, retainerPeriodPanelModel} from '../logic/retainerPeriodPanelModel';

import type {
	TimeRegistrationModalRetainerPeriodQuery$data,
	TimeRegistrationModalRetainerPeriodQuery$variables,
} from './__generated__/TimeRegistrationModalRetainerPeriodQuery.graphql';

const query = graphql`
	query TimeRegistrationModalRetainerPeriodQuery(
		$projectId: ID!
		$year: Int!
		$month: Int!
		$day: Int!
		$taskId: ID
		$excludeTimeRegId: ID
	) {
		viewer {
			project(internalId: $projectId) {
				retainerPeriodStatus(
					year: $year
					month: $month
					day: $day
					taskId: $taskId
					excludeTimeRegId: $excludeTimeRegId
				) {
					name
					budgetType
					locked
					targetMinutes
					registeredMinutes
					entryCounts
					moveRefused
				}
			}
		}
	}
`;

const {color, spacing} = tokensForThemeProvider.sky.sys;

// Registered within target, this entry within target, the rest of the target, registered over, this entry over.
const SEGMENT_COLORS = [
	color.iconfill.success,
	color.iconfill.success,
	'transparent',
	color.iconfill.warning,
	color.iconfill.warning,
];
const SEGMENT_OPACITY = [1, 0.5, 1, 1, 0.5];

// The panel is a card, bordered like the modal's inputs.
const PanelCard = styled.div`
	background-color: ${color.background.neutral['1']};
	border: ${inputTokens.border.default};
	border-radius: ${inputTokens.borderRadius};
	padding: ${spacing['300']};
`;

// The bar and its target label, with room above, and below unless the label already takes it.
const Graph = styled.div<{labelled: boolean}>`
	padding: ${spacing['200']} 0 ${({labelled}) => (labelled ? 0 : spacing['200'])};
`;

// Hours over target in the design's dark orange.
const OverTarget = styled.span`
	color: ${color.graph['9']};
`;

const Bar = styled.div`
	display: flex;
	width: 100%;
	height: ${spacing['200']};
	border-radius: ${spacing['100']};
	overflow: hidden;
	background-color: ${color.background.disabled['1']};
`;

const BarWithTargetLine = styled.div`
	position: relative;
	width: 100%;
`;

// The target line sticks out above and below the bar, where the target ends.
const TargetLine = styled.div<{at: number}>`
	position: absolute;
	top: -${spacing['100']};
	bottom: -${spacing['100']};
	left: ${({at}) => at * 100}%;
	width: 2px;
	margin-left: -1px;
	background-color: ${color.text.content.main};
`;

// The "Target" label, its right edge on the target line.
const TargetLabelRow = styled.div`
	position: relative;
	height: ${spacing['500']};
`;

const TargetLabel = styled.div<{at: number}>`
	position: absolute;
	right: ${({at}) => (1 - at) * 100}%;
	padding-right: ${spacing['100']};
	white-space: nowrap;
`;

const Segment = styled.div<{grow: number; segmentColor: string; segmentOpacity: number}>`
	flex: ${({grow}) => grow} 0 0;
	background-color: ${({segmentColor}) => segmentColor};
	opacity: ${({segmentOpacity}) => segmentOpacity};
`;

type Props = {
	projectId: string | null;
	taskId: string | null;
	date: Date;
	entryBillableMinutes: number;
	timeRegistrationId?: string;
	/** Told whether the entry's move into this date's period is refused, so the modal can disable Save. */
	onMoveRefusedChange?: (refused: boolean) => void;
};

export const TimeRegistrationModalRetainerPeriod = ({
	projectId,
	taskId,
	date,
	entryBillableMinutes,
	timeRegistrationId,
	onMoveRefusedChange,
}: Props): JSX.Element | null => {
	const intl = useIntl();
	const {fetch} = useForecastFetchQuery<
		TimeRegistrationModalRetainerPeriodQuery$data,
		TimeRegistrationModalRetainerPeriodQuery$variables
	>(query);
	// The answer for the current date and entity only: the hook's `data` holds whichever response landed last.
	const [status, setStatus] = useState<RetainerPeriodStatus | null>(null);

	const year = date.getFullYear();
	const month = date.getMonth() + 1;
	const day = date.getDate();

	// The entry's hours are applied here rather than sent, so typing does not refetch. Until the new answer lands,
	// and if it fails, nothing shows; an answer for a date or entity since left behind is dropped.
	useEffect(() => {
		setStatus(null);
		if (!projectId) return;
		let current = true;
		fetch({projectId, taskId: taskId ?? undefined, year, month, day, excludeTimeRegId: timeRegistrationId}, data => {
			if (current) setStatus(data?.viewer?.project?.retainerPeriodStatus ?? null);
		});
		return () => {
			current = false;
		};
	}, [projectId, taskId, year, month, day, timeRegistrationId]);

	const moveRefused = !!projectId && !!status?.moveRefused;
	useEffect(() => {
		onMoveRefusedChange?.(moveRefused);
	}, [moveRefused]);

	if (!projectId) return null;

	const model = retainerPeriodPanelModel(status, entryBillableMinutes);
	const hours = (minutes: number) => convertMinutesToFullHour(minutes, intl, true);
	const boldHours = (minutes: number) => <strong>{hours(minutes)}</strong>;

	switch (model.kind) {
		case 'none':
			return null;
		case 'moveRefused':
			return (
				<Alert
					type={AlertType.WARNING}
					text={intl.formatMessage({id: 'retainer.period_status.move_refused'}, {period: model.periodName})}
				/>
			);
		case 'locked':
			return (
				<Alert
					type={AlertType.WARNING}
					text={intl.formatMessage({id: 'retainer.period_status.locked'}, {period: model.periodName})}
				/>
			);
		case 'numbers':
			return (
				<FlexColumn gap="s">
					<PanelCard>
						<FlexColumn gap="xs">
							<FlexRow justifyContent="space-between">
								<Text>
									<strong>{model.periodName}</strong>
								</Text>
								<Text color="medium">
									{intl.formatMessage(
										{id: 'retainer.period_status.target'},
										{hours: hours(model.targetMinutes)}
									)}
								</Text>
							</FlexRow>
							<Graph labelled={model.showDivider}>
								<BarWithTargetLine>
									<Bar>
										{model.segments.map((grow, index) => (
											<Segment
												key={index}
												grow={grow}
												segmentColor={SEGMENT_COLORS[index]}
												segmentOpacity={SEGMENT_OPACITY[index]}
											/>
										))}
									</Bar>
									{model.showDivider && (
										<TargetLine at={model.targetFraction} data-cy="retainer-period-target-line" />
									)}
								</BarWithTargetLine>
								{model.showDivider && (
									<TargetLabelRow>
										<TargetLabel at={model.targetFraction} data-cy="retainer-period-target-label">
											<Text size="2">
												{intl.formatMessage({id: 'retainer.period_status.bar_target'})}
											</Text>
										</TargetLabel>
									</TargetLabelRow>
								)}
							</Graph>
							<FlexRow justifyContent="space-between">
								<Text size="2" color="main">
									{intl.formatMessage(
										{id: 'retainer.period_status.registered'},
										{hours: boldHours(model.registeredMinutes)}
									)}
								</Text>
								<Text size="2" color="main">
									{model.overMinutes > 0 ? (
										<OverTarget data-cy="retainer-period-over-target">
											{intl.formatMessage(
												{id: 'retainer.period_status.over'},
												{hours: boldHours(model.overMinutes)}
											)}
										</OverTarget>
									) : (
										intl.formatMessage(
											{id: 'retainer.period_status.remaining'},
											{hours: boldHours(model.remainingMinutes)}
										)
									)}
								</Text>
							</FlexRow>
						</FlexColumn>
					</PanelCard>
					{model.entryOverMinutes > 0 && (
						<Alert
							type={AlertType.WARNING}
							text={intl.formatMessage(
								{id: 'retainer.period_status.over_message'},
								{hours: hours(model.entryOverMinutes)}
							)}
						/>
					)}
				</FlexColumn>
			);
	}
};
